import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell, DynamicIconWrapper, Icons } from '@components/common';
import Actions from '@components/common/Actions';

import { IConfigurationTemplate } from '@interfaces/cmModule/configurationTemplates';
import { TTreeNode } from '@interfaces/cmModule/goldenSettings';
import { IBasicNode } from '@interfaces/components/tree';
import { IFlatXMLTreeNode, INonCompactXMLData, TMapTree } from '@components/common/FlatXMLTree/interface';

import { ETreeTypeNode } from '@enums/tree';

import { FLAT_NODE_PATH_SEPARATOR } from '@constants/common';
import { ECompactXMLDataType } from '@components/common/FlatXMLTree/constants';

import styles from './ConfigurationTemplates.module.scss';

export const getSkippedXmlNodes = (data: TMapTree): string[] => {
	const skippedNodes: string[] = [];

	data.forEach(node => {
		if (node.isChecked) {
			skippedNodes.push(node.keysPath);
		}
	});

	return skippedNodes;
};

export const getPathForArrayObject = (node: IBasicNode) => {
	const keysList = node?.keysPath?.split(FLAT_NODE_PATH_SEPARATOR);
	const path = node?.keysPath?.endsWith(node.key) ? keysList?.slice(0, -1) : keysList;

	return node?.nodeType === ETreeTypeNode.arrayObjects || keysList?.length === 1
			? node.keysPath + FLAT_NODE_PATH_SEPARATOR
			: path?.join(FLAT_NODE_PATH_SEPARATOR) + FLAT_NODE_PATH_SEPARATOR;
};

export const convertXmlDataToMoTree = (data: TMapTree): TTreeNode => {
	if (!data || data.size === 0) {
		return {} as TTreeNode;
	}

	const nodesArray = Array.from(data.values());

	const rootNode = nodesArray.find(node => node.depth === 0);

	if (!rootNode) {
		return {} as TTreeNode;
	}

	const processNode = (node: IFlatXMLTreeNode): TTreeNode => {
		const treeNode: TTreeNode = {
			name: node.key,
			value: node.value || undefined,
			description: node.description,
			subNodes: [],
			type: node.nodeType || ETreeTypeNode.parameter,
			path: getPathForArrayObject(node)!,
			categoryId: '',
		};

		const childrenNodes = nodesArray.filter(
			childNode => childNode.parentPath === node.path && childNode.id !== node.id,
		);

		if (childrenNodes.length > 0) {
			treeNode.subNodes = childrenNodes.map(childNode => processNode(childNode));
		}

		return treeNode;
	};

	return processNode(rootNode);
};

export const convertMoTreeToXmlData = (moTreeData: TTreeNode): INonCompactXMLData[] => {
	if (!moTreeData) {
		return [];
	}

	const processNode = (node: TTreeNode): INonCompactXMLData => {
		const result: INonCompactXMLData = {
			type: ECompactXMLDataType.element,
			name: node.name || '',
			elements: [],
			nodeType: node.type !== ETreeTypeNode.parameter ? node.type : undefined,
		};

		if (node.value !== undefined && node.value !== null) {
			result.elements.push({
				type: ECompactXMLDataType.text,
				text: String(node.value),
				name: '',
				elements: [],
			});
		}

		if (node.values && node.values.length > 0) {
			node.values.forEach(val => {
				result.elements.push({
					type: ECompactXMLDataType.text,
					text: String(val),
					name: '',
					elements: [],
				});
			});
		}

		if (node.subNodes && node.subNodes.length > 0) {
			result.elements = [
				...result.elements,
				...node.subNodes.map(child => processNode(child)),
			];
		}

		if (node.description) {
			result.attributes = {
				xmlns: node.description,
			};
		}

		return result;
	};

	if (Array.isArray(moTreeData)) {
		return moTreeData.map(node => processNode(node));
	}

	return [processNode(moTreeData)];
};

interface IGetConfigurationTemplateColumns {
	setSelectedTemplate: (template: IConfigurationTemplate) => void;
	openNotesModal: () => void;
	openTemplateModal: (template: IConfigurationTemplate, isCloneMode?: boolean) => void;
	openViewTreeModal: (template: IConfigurationTemplate) => void;
	openDeleteTemplateModal: (template: IConfigurationTemplate) => void;
	openConsistencyCheck: (id: string) => void;
	isViewerOnly?: boolean;
}

export const getConfigurationTemplateColumns = ({
	setSelectedTemplate,
	openNotesModal,
	openTemplateModal,
	openViewTreeModal,
	openDeleteTemplateModal,
	openConsistencyCheck,
	isViewerOnly,
}: IGetConfigurationTemplateColumns) => ([
	{
		header: 'Name',
		accessorKey: 'name',
		enableSorting: true,
		cell: ({ row: { original } }: CellContext<IConfigurationTemplate, any>) => {
			const { name } = original;

			return (
				<div className={styles.notesWrapper}>
					<span>{name}</span>
				</div>
			);
		},
	},
	{
		header: 'Config Type',
		accessorKey: 'configType',
		enableSorting: true,
		cell: ({ getValue }: CellContext<IConfigurationTemplate, any>) => {
			const { title } = getValue();

			return <AdaptiveTableCell text={title} />;
		},
	},
	{
		header: () => <>YANG Model<br/>collections</>,
		accessorKey: 'validationRulesFolderName',
		enableSorting: true,
		cell: ({ row: { original } }: CellContext<IConfigurationTemplate, any>) => {
			const { validationRulesFolderName } = original;

			return <AdaptiveTableCell text={validationRulesFolderName || '-'} />;
		},
	},
	{
		header: () => <>Consistency<br/>Check</>,
		accessorKey: 'id',
		enableSorting: true,
		cell: ({ row: { original } }: CellContext<IConfigurationTemplate, any>) => {
			const { isHistoryAvailable, id } = original || {};

			const onClick = () => {
				if (!isHistoryAvailable) {
					return;
				}

				openConsistencyCheck(id);
			};

			return (
				isHistoryAvailable
				? (
					<div className={styles.linkWrapper}>
						<span
							className={styles.linkText}
							onClick={onClick}
						>
							View
						</span>
						<DynamicIconWrapper
							icon={Icons.viewIcon}
							onClick={onClick}
						/>
					</div>
				)
				: <AdaptiveTableCell text='N/A' />
			);
		},
	},
	{
		header: 'Created',
		accessorKey: 'createdAtText',
		enableSorting: true,
		cell: ({ getValue }: CellContext<IConfigurationTemplate, any>) => {
			const triggerCondition = getValue();

			return <AdaptiveTableCell text={triggerCondition} />;
		},
	},
	{
		header: 'Updated',
		accessorKey: 'updatedAtText',
		enableSorting: true,
		cell: ({ getValue }: CellContext<IConfigurationTemplate, any>) => {
			const triggerCondition = getValue();

			return <AdaptiveTableCell text={triggerCondition} />;
		},
	},
	{
		header: 'User',
		accessorKey: 'userName',
		enableSorting: true,
		cell: ({ getValue }: CellContext<IConfigurationTemplate, any>) => {
			const triggerCondition = getValue();

			return <AdaptiveTableCell text={triggerCondition} />;
		},
	},
	{
		header: 'Notes',
		accessorKey: 'notes',
		cell: ({ row: { original } }: CellContext<IConfigurationTemplate, any>) => {
			const { notes } = original;

			const handleOpenNotes = () => {
				openNotesModal();
				setSelectedTemplate(original);
			};

			const isNoteExist = !!notes;

			return (
				<div className={styles.notesWrapper}>
					{isNoteExist
						? (
							<>
								<DynamicIconWrapper
									icon={Icons.description}
									onClick={handleOpenNotes}
									tooltipLabel='View note'
								/>
							</>
						)
						: '-'
					}
				</div>
			);
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<IConfigurationTemplate, any>) => {
			const isDeleteAvailable = row.original.isDeleteAvailable;

			const editHandler = () => openTemplateModal(row.original);
			const viewHandler = () => openViewTreeModal(row.original);
			const handleDelete = () => openDeleteTemplateModal(row.original);
			const handleClone = () => openTemplateModal(row.original, true);

			return (
				<Actions
					cloneTooltip='Clone template'
					deleteTooltip='Delete template'
					editHandler={editHandler}
					editTooltip='Edit details'
					handleClone={handleClone}
					handleDelete={handleDelete}
					isClone={!isViewerOnly}
					isDelete={!isViewerOnly && isDeleteAvailable}
					isEdit={!isViewerOnly}
					isView
					viewHandler={viewHandler}
					viewTooltip='View tree'
				/>
			);
		},
	},
]);
